/**
 * Automatic free gifts.
 *
 * Keeps up to three configured gift products in the cart, each with its own minimum purchase
 * requirement — none, a subtotal, or a quantity, the same three choices Shopify's discounts
 * offer. Every slot is evaluated on every pass, so a cart can hold none, one, or all three gifts
 * at once. Reconciliation runs after every cart update (add to cart, quantity change, removal)
 * and, as a safety net, right before checkout.
 *
 * "Buy it now" is handled separately: it normally skips the cart entirely and checks out with
 * just the one product, which no gift can reach. When `addOnBuyNow` is set, the click is
 * intercepted, the product is added to the cart, the gifts are reconciled, and the customer
 * continues to the regular checkout. Wallet buttons (Shop Pay, Apple Pay, Google Pay, PayPal)
 * render inside a cross-origin frame and cannot be intercepted at all.
 *
 * The gifts' prices are handled by automatic discounts in the Shopify admin — this module only
 * manages the cart lines.
 *
 * Configuration comes from `snippets/free-gift.liquid`, which renders each gift's variants
 * server-side so inventory levels (unavailable to the cart AJAX API) can drive variant choice.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** Tags the events this module dispatches so its own listener doesn't react to them. */
const OWN_EVENT = Symbol('free-gift');

/** Longest a checkout click waits for the gifts before continuing without them. */
const CHECKOUT_TIMEOUT = 3000;

const CHECKOUT_TRIGGER_SELECTOR = '[name="checkout"], .cart__checkout-button, a[href$="/checkout"]';

/** Shopify's own dynamic checkout markup. Wallet buttons live in a frame and never match. */
const BUY_NOW_TRIGGER_SELECTOR =
  '.shopify-payment-button__button--unbranded, .shopify-payment-button__more-options';

const PAYMENT_BUTTON_SELECTOR = 'shopify-payment-button, .shopify-payment-button';

/** The form Shopify's button would have submitted, and the source of the fields we post. */
const PRODUCT_FORM_SELECTOR = 'form[data-type="add-to-cart-form"], form[action*="/cart/add"]';

const SOURCE = 'free-gift';

/**
 * @typedef {object} FreeGiftVariant
 * @property {number} id - The variant ID.
 * @property {number} inventory - The variant's inventory quantity at render time.
 * @property {boolean} tracked - Whether Shopify tracks inventory for the variant.
 *
 * @typedef {object} FreeGift
 * @property {string} slot - The settings group this gift came from, used in log messages.
 * @property {number} productId - The gift product ID.
 * @property {FreeGiftVariant[]} variants - The gift product's available variants.
 * @property {number} quantity - How many of the gift the cart should hold.
 * @property {'none' | 'subtotal' | 'quantity'} minimumRequirement - Which threshold gates this
 *   gift. `none` means any non-empty cart qualifies.
 * @property {number} minimumSubtotal - Subtotal the rest of the cart must reach, in the
 *   currency's subunit. Only consulted when `minimumRequirement` is `subtotal`.
 * @property {number} minimumQuantity - Units the rest of the cart must hold. Only consulted when
 *   `minimumRequirement` is `quantity`.
 * @property {'most_stock' | 'first_available'} variantPriority - Which variant to reach for first.
 *
 * @typedef {object} FreeGiftConfig
 * @property {FreeGift[]} gifts - The enabled gifts, in settings order.
 * @property {boolean} addOnCheckout - Whether to reconcile when a checkout button is clicked.
 * @property {boolean} addOnBuyNow - Whether to route "Buy it now" through the cart so the gifts
 *   can reach the order.
 * @property {string} checkoutUrl - Locale-aware checkout URL, used only as a navigation fallback.
 * @property {string} template - The current template name.
 * @property {number} cartItemCount - The cart item count at render time.
 */

/** @type {FreeGiftConfig | null} */
const config = readConfig();

/** Serializes reconciliation so overlapping cart updates can't add a gift twice. */
let queue = Promise.resolve();

if (config) init();

/** @returns {FreeGiftConfig | null} */
function readConfig() {
  const element = document.getElementById('free-gift-config');
  if (!element?.textContent) return null;

  try {
    const parsed = JSON.parse(element.textContent);
    const gifts = normalizeGifts(parsed.gifts);

    return gifts.length > 0 ? { ...parsed, gifts } : null;
  } catch (error) {
    console.warn('[free-gift] Invalid configuration', error);
    return null;
  }
}

/**
 * Fills in the defaults a cached page from before a setting existed would be missing, and drops
 * any slot repeating an earlier slot's product — two slots managing one product would fight over
 * its quantity on every pass.
 *
 * @param {any} gifts
 * @returns {FreeGift[]}
 */
function normalizeGifts(gifts) {
  if (!Array.isArray(gifts)) return [];

  /** @type {FreeGift[]} */
  const normalized = [];
  const claimed = new Set();

  for (const gift of gifts) {
    const productId = Number(gift?.productId);
    if (!productId) continue;

    if (claimed.has(productId)) {
      console.warn(`[free-gift] ${gift.slot} repeats product ${productId}; ignoring that slot`);
      continue;
    }

    claimed.add(productId);

    normalized.push({
      ...gift,
      productId,
      quantity: Math.max(1, Number(gift.quantity) || 1),
      minimumRequirement: gift.minimumRequirement ?? 'none',
      minimumSubtotal: Math.max(0, Number(gift.minimumSubtotal) || 0),
      minimumQuantity: Math.max(0, Number(gift.minimumQuantity) || 0),
      variants: Array.isArray(gift.variants) ? gift.variants : [],
    });
  }

  return normalized;
}

function init() {
  document.addEventListener(StandardEvents.cartLinesUpdate, onCartLinesUpdate);

  if (config?.addOnCheckout) {
    // Capture phase so the gifts land in the cart before the form submits or the link navigates.
    document.addEventListener('click', onCheckoutClick, true);
  }

  // Capture phase again, and for the same reason: Shopify's own handler is on the button, so
  // stopping the event here is the only way to get in front of it.
  if (config?.addOnBuyNow) document.addEventListener('click', onBuyNowClick, true);

  // Carts built before the gifts were configured (or before this script existed) get fixed up
  // when the customer lands on the cart page.
  if (config?.template === 'cart' && config.cartItemCount > 0) reconcile();
}

/**
 * @param {CartLinesUpdateEvent} event
 */
function onCartLinesUpdate(event) {
  if (/** @type {any} */ (event)[OWN_EVENT]) return;

  const { promise } = event;

  if (!promise) {
    reconcile();
    return;
  }

  promise
    .then(({ detail } = {}) => {
      if (!detail?.didError) reconcile();
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[free-gift] Event promise rejected:', error);
    });
}

/**
 * Queues a reconciliation pass.
 * @returns {Promise<void>}
 */
function reconcile() {
  queue = queue.then(run).catch((error) => {
    console.warn('[free-gift] Reconciliation failed', error);
  });

  return queue;
}

/**
 * Brings the cart in line with every gift's rules: each one present when the rest of the cart
 * meets its minimum purchase requirement, absent when it doesn't, and never more than the
 * configured quantity.
 */
async function run() {
  if (!config) return;

  const cart = await fetchCart();
  const giftProductIds = new Set(config.gifts.map((gift) => gift.productId));

  /** @type {Map<number, Array<{key: string, quantity: number}>>} */
  const giftLines = new Map();
  let qualifyingLineCount = 0;
  let qualifyingSubtotal = 0;
  let qualifyingQuantity = 0;

  for (const item of cart.items) {
    if (giftProductIds.has(item.product_id)) {
      const lines = giftLines.get(item.product_id) ?? [];
      lines.push(item);
      giftLines.set(item.product_id, lines);
      continue;
    }

    qualifyingLineCount += 1;
    qualifyingSubtotal += lineSubtotal(item);
    qualifyingQuantity += lineQuantity(item);
  }

  // Every gift measures against the same totals, which exclude all gift lines — so one gift can
  // never push the cart over another's threshold, and the gifts can't cascade into each other.
  // The slots settle one at a time (the cart API takes one write at a time), and a mutation on
  // one product never changes another product's line key, so this snapshot stays valid.
  for (const gift of config.gifts) {
    const earned =
      qualifyingLineCount > 0 && meetsMinimum(gift, qualifyingSubtotal, qualifyingQuantity);

    await settle(gift, giftLines.get(gift.productId) ?? [], earned);
  }
}

/**
 * Adds, trims, or removes one gift's cart lines.
 *
 * @param {FreeGift} gift
 * @param {Array<{key: string, quantity: number}>} lines - The gift's lines currently in the cart.
 * @param {boolean} earned - Whether the cart qualifies for the gift.
 */
async function settle(gift, lines, earned) {
  // Nothing to gift against, or the cart falls short of the requirement. A cart holding only
  // gifts can never be emptied otherwise.
  if (!earned) {
    await removeLines(lines);
    return;
  }

  if (lines.length === 0) {
    await addGift(gift);
    return;
  }

  // Collapse any extra lines (possible when the customer adds the gift themselves with
  // different line properties) and keep the remaining one at the configured quantity.
  const [primary, ...duplicates] = lines;
  await removeLines(duplicates);

  if (primary && primary.quantity !== gift.quantity) {
    await changeLine(primary.key, gift.quantity);
  }
}

/**
 * Whether the cart clears the threshold this gift is gated on. Only the requirement the merchant
 * picked is consulted; the other threshold stays configured but dormant, so switching between
 * them in the theme editor takes effect without re-entering values.
 *
 * @param {FreeGift} gift
 * @param {number} subtotal - Subtotal of the non-gift lines, in the currency's subunit.
 * @param {number} quantity - Units held by the non-gift lines.
 * @returns {boolean}
 */
function meetsMinimum(gift, subtotal, quantity) {
  switch (gift.minimumRequirement) {
    case 'subtotal':
      return subtotal >= gift.minimumSubtotal;
    case 'quantity':
      return quantity >= gift.minimumQuantity;
    default:
      return true;
  }
}

/**
 * What a cart line contributes towards a minimum subtotal, in the currency's subunit.
 * `final_line_price` is the amount after any discount the line carries — what the customer
 * actually spends — and is what the threshold should be measured against.
 *
 * @param {any} item
 * @returns {number}
 */
function lineSubtotal(item) {
  const price = item.final_line_price ?? item.line_price ?? item.original_line_price;
  return typeof price === 'number' ? price : 0;
}

/**
 * What a cart line contributes towards a minimum quantity: units, matching how Shopify's
 * discounts count a "minimum quantity of items".
 *
 * @param {any} item
 * @returns {number}
 */
function lineQuantity(item) {
  return typeof item.quantity === 'number' ? item.quantity : 0;
}

/**
 * Adds one gift, walking its variant list until one succeeds. Inventory is a render-time
 * snapshot, so the preferred variant can be gone by the time we ask for it.
 *
 * @param {FreeGift} gift
 */
async function addGift(gift) {
  for (const variant of orderedVariants(gift)) {
    try {
      await mutate({
        request: () =>
          postCart(Theme.routes.cart_add_url, { items: [{ id: variant.id, quantity: gift.quantity }] }),
        resolveLines: (cart) =>
          cart.items
            .filter(/** @param {any} item */ (item) => item.variant_id === variant.id)
            .map(/** @param {any} item */ (item) => ({ id: item.key, quantity: item.quantity })),
      });
      return;
    } catch (error) {
      console.warn(`[free-gift] Could not add variant ${variant.id}`, error);
    }
  }
}

/**
 * @param {FreeGift} gift
 * @returns {FreeGiftVariant[]}
 */
function orderedVariants(gift) {
  if (gift.variantPriority !== 'most_stock') return gift.variants;

  // Sort is stable, so equal stock levels keep the merchant's variant order.
  return [...gift.variants].sort((a, b) => stockLevel(b) - stockLevel(a));
}

/**
 * @param {FreeGiftVariant} variant
 * @returns {number}
 */
function stockLevel(variant) {
  // Untracked inventory is effectively unlimited, so it outranks any counted stock.
  return variant.tracked ? variant.inventory : Number.MAX_SAFE_INTEGER;
}

/**
 * @param {Array<{key: string}>} lines
 */
async function removeLines(lines) {
  for (const line of lines) {
    await changeLine(line.key, 0);
  }
}

/**
 * @param {string} key - The cart line key.
 * @param {number} quantity - The new quantity.
 */
function changeLine(key, quantity) {
  return mutate({
    action: quantity === 0 ? 'remove' : 'update',
    request: () => postCart(Theme.routes.cart_change_url, { id: key, quantity }),
    resolveLines: () => [{ id: key, quantity }],
  });
}

/**
 * Performs a cart mutation and tells the rest of the theme about it so the cart drawer, cart
 * page and header count re-render.
 *
 * The event is dispatched after the request succeeds — a failed background mutation is a no-op
 * that no listener needs to hear about.
 *
 * @param {object} options
 * @param {'update' | 'remove'} [options.action] - Defaults to `update`. Adding a gift is
 *   deliberately reported as an update: an `add` makes the cart drawer auto-open, which would
 *   steal focus from a quick-add modal or fire while the customer is heading to checkout.
 * @param {() => Promise<any>} options.request - The cart API call to run.
 * @param {(cart: any) => Array<{id: string, quantity: number}>} options.resolveLines - The
 *   affected lines, read off the updated cart so the payload carries real line keys.
 */
async function mutate({ action = 'update', request, resolveLines }) {
  const response = await request();
  const cart = await fetchCart();
  const lines = resolveLines(cart);

  if (lines.length === 0) return;

  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise });
  /** @type {any} */ (event)[OWN_EVENT] = true;

  document.dispatchEvent(event);

  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      itemCount: cart.item_count,
      sections: response?.sections,
      source: SOURCE,
      didError: false,
    },
  });
}

/**
 * @param {string} url
 * @param {Record<string, unknown>} body
 */
async function postCart(url, body) {
  const sections = cartSectionIds();

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({
      ...body,
      ...(sections && { sections, sections_url: window.location.pathname }),
    }),
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `Cart request failed (${response.status})`);
  }

  return data ?? {};
}

async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

  return response.json();
}

/**
 * Section IDs the cart API should re-render alongside the mutation.
 * @returns {string}
 */
function cartSectionIds() {
  const ids = new Set();

  for (const element of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const { sectionId } = /** @type {HTMLElement} */ (element).dataset;
    if (sectionId) ids.add(sectionId);
  }

  return Array.from(ids).join(',');
}

/**
 * Lets the replayed checkout click through. Kept off the element because adding a gift
 * re-renders the cart, and `morph` drops attributes the server didn't send back.
 */
let replayingCheckout = false;

/** Guards against a second click landing while the first is still reconciling. */
let checkoutPending = false;

/**
 * @param {MouseEvent} event
 */
async function onCheckoutClick(event) {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest(CHECKOUT_TRIGGER_SELECTOR);
  if (!(trigger instanceof HTMLElement)) return;

  // Dynamic checkout buttons are onBuyNowClick's job: the product isn't in the cart yet, so
  // reconciling and continuing would check out without it.
  if (trigger.closest(PAYMENT_BUTTON_SELECTOR)) return;

  if (replayingCheckout) {
    replayingCheckout = false;
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (checkoutPending) return;
  checkoutPending = true;

  trigger.setAttribute('aria-busy', 'true');

  try {
    // A slow or failing cart request must never trap the customer on the cart.
    await Promise.race([reconcile(), wait(CHECKOUT_TIMEOUT)]);
  } finally {
    checkoutPending = false;
    trigger.removeAttribute('aria-busy');
  }

  // Replay the click now that the gifts are in place. If re-rendering the cart replaced the
  // button, navigate to checkout directly instead.
  if (trigger.isConnected) {
    replayingCheckout = true;
    try {
      trigger.click();
    } finally {
      replayingCheckout = false;
    }
  } else {
    window.location.href = config?.checkoutUrl ?? '/checkout';
  }
}

/** Lets the handed-back Buy it now click through when we could not add the product ourselves. */
let replayingBuyNow = false;

/** Guards against a second click landing while the first is still adding to the cart. */
let buyNowPending = false;

/**
 * Turns "Buy it now" into "add to cart, then check out". Shopify's button builds a checkout from
 * the product form alone, bypassing the cart, so a gift can only reach the order if the purchase
 * goes through the cart first.
 *
 * The customer therefore checks out with their whole cart rather than just this product. That is
 * the trade-off the setting turns on and off.
 *
 * @param {MouseEvent} event
 */
async function onBuyNowClick(event) {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(event.target instanceof Element)) return;

  const trigger = event.target.closest(BUY_NOW_TRIGGER_SELECTOR);
  if (!(trigger instanceof HTMLElement)) return;

  if (replayingBuyNow) {
    replayingBuyNow = false;
    return;
  }

  // No form to read the variant, quantity and properties from — leave Shopify's flow alone.
  const form = trigger.closest(PRODUCT_FORM_SELECTOR);
  if (!(form instanceof HTMLFormElement)) return;

  event.preventDefault();
  event.stopPropagation();

  if (buyNowPending) return;
  buyNowPending = true;

  trigger.setAttribute('aria-busy', 'true');

  let added = false;

  try {
    await addFromForm(form);
    added = true;

    // The gifts are worth waiting for, but never at the cost of stranding the customer.
    await Promise.race([reconcile(), wait(CHECKOUT_TIMEOUT)]);
  } catch (error) {
    console.warn('[free-gift] Buy it now could not go through the cart', error);
  } finally {
    buyNowPending = false;
    trigger.removeAttribute('aria-busy');
  }

  if (added) {
    window.location.href = config?.checkoutUrl ?? '/checkout';
    return;
  }

  // The product never made it into the cart, so hand the click back to Shopify rather than send
  // the customer to a checkout that is missing the thing they wanted to buy.
  if (trigger.isConnected) {
    replayingBuyNow = true;
    try {
      trigger.click();
    } finally {
      replayingBuyNow = false;
    }
  }
}

/**
 * Adds the product the customer clicked Buy it now on, posting the product form's own fields so
 * the variant, quantity, line item properties and selling plan all carry over — the same payload
 * the theme's add to cart button sends.
 *
 * @param {HTMLFormElement} form
 */
async function addFromForm(form) {
  const body = new FormData(form);
  const sections = cartSectionIds();

  if (sections) {
    body.set('sections', sections);
    body.set('sections_url', window.location.pathname);
  }

  // No Content-Type header: the browser has to set the multipart boundary itself.
  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
    body,
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new Error(data?.description || data?.message || `Cart request failed (${response.status})`);
  }
}

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
